import { useParams } from 'react-router';
import { useQuery } from '@apollo/client';
import { Box, Container, Typography, CircularProgress, Card, Alert } from '@mui/joy';
import { Video } from './VideoJs';
import { INTERVIEW_FOR_VIDEO_PLAYER_GQL } from '../gql/common/videoPlayer.query';
import { RECORDING_STATES, RECORDING_STATES_LABELS } from '../constants';

const VideoPlayer = () => {
  const { interviewId } = useParams();
  const { data, loading, error } = useQuery(INTERVIEW_FOR_VIDEO_PLAYER_GQL, {
    variables: { id: interviewId },
    skip: !interviewId,
  });

  const interview = data?.interviewForVideoPlayer;
  const recording = interview?.recording;
  const isReady = recording?.state === RECORDING_STATES.READY;

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.level1',
        }}
      >
        <CircularProgress size="lg" variant="soft" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.level1',
          p: 3,
        }}
      >
        <Container maxWidth="md">
          <Alert color="danger" variant="soft" size="lg">
            <Typography level="title-lg" sx={{ mb: 1 }}>
              Error Loading Video
            </Typography>
            <Typography level="body-md">
              {error.message || 'Unable to load the interview. Please check the link and try again.'}
            </Typography>
          </Alert>
        </Container>
      </Box>
    );
  }

  if (!interview || !recording) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.level1',
          p: 3,
        }}
      >
        <Container maxWidth="md">
          <Alert color="warning" variant="soft" size="lg">
            <Typography level="title-lg" sx={{ mb: 1 }}>
              Interview Not Found
            </Typography>
            <Typography level="body-md">
              The requested interview could not be found. Please verify the link.
            </Typography>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.surface',
        py: { xs: 4, sm: 6, md: 8 },
        px: 2,
      }}
    >
      <Container maxWidth="lg">
        {/* <Box
          sx={{
            mb: 4,
            textAlign: 'center',
          }}
        >
          <Typography
            level="h1"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 800,
              mb: 2,
            }}
          >
            Interview Recording
          </Typography>
          <Typography
            level="body-lg"
            sx={{
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Review the candidate&apos;s video interview below
          </Typography>
        </Box> */}

        {!isReady ? (
          <Card
            variant="soft"
            sx={{
              maxWidth: '600px',
              mx: 'auto',
              p: 4,
              textAlign: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <CircularProgress size="lg" />
            </Box>
            <Typography level="title-lg" sx={{ mb: 1, color: 'neutral.800' }}>
              {RECORDING_STATES_LABELS[recording.state] || 'Processing...'}
            </Typography>
            <Typography level="body-md" sx={{ color: 'neutral.600' }}>
              {recording.state === RECORDING_STATES.ERROR
                ? 'There was an error processing this recording. Please contact support.'
                : 'Please check back in a few moments.'}
            </Typography>
          </Card>
        ) : (
          <Card
            variant="plain"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: 'xl',
            }}
          >
            <Video
              recordingId={recording._id}
              durations={interview.durations || []}
            />
          </Card>
        )}

        <Box
          sx={{
            mt: 6,
            textAlign: 'center',
          }}
        >
          <Typography
            level="body-sm"
            sx={{
              color: 'text.tertiary',
            }}
          >
            © {new Date().getFullYear()} Cavalry. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default VideoPlayer;
